"use client";

import { Button } from "@/components/ui/button";
import Image from "next/image";
import { Header } from "@/components/header";
import { HeroSection } from "@/components/hero-section";
import { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";
import { productsApi, categoryApi, teamsApi, contentInfoApi } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

import dynamic from "next/dynamic";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

interface Category {
  id: string;
  name_category: string;
}

interface Team {
  id: string;
  name_team: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  slug: string;
  logo: string;
  key_features: string;
  id_category: string;
  id_team: string;
  is_active: boolean;
  display_order: number;
}

interface Solution {
  id: string;
  category: string;
  title: string;
  teamName: string;
  description: string;
  buttonText: string;
  features: string[];
  image: string;
  imageAlt: string;
  isPlaceholder: boolean;
  link: string;
}

interface ContentInfo {
  id_content: string;
  content_title: string;
  content_description?: string | null;
  type?: string | null;
}

export default function Solutions() {
  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroContent, setHeroContent] = useState<ContentInfo | null>(null);
  const { language } = useLanguage();

  const labels = {
    en: {
      visit: "Visit",
      product: "Product",
      keyFeatures: "Key Features:",
      loading: "Loading solutions...",
      error: "Failed to load solutions",
      noSolutions: "No solutions available",
    },
    id: {
      visit: "Kunjungi",
      product: "Produk",
      keyFeatures: "Fitur Utama:",
      loading: "Memuat solusi...",
      error: "Gagal memuat solusi",
      noSolutions: "Tidak ada solusi tersedia",
    },
  };

  const t = labels[language];

  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "solution", lang: language });
        const data = (response.data?.data || response.data) as ContentInfo[];

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching solutions hero content:", err);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all required data in parallel
        const [productsRes, categoriesRes, teamsRes] = await Promise.all([
          productsApi.getAll({ limit: 100, lang: language }),
          categoryApi.getAll({ limit: 100, lang: language }),
          teamsApi.getAll({ limit: 100 }),
        ]);

        const products = (productsRes.data.data || productsRes.data) as Product[];
        const categories = (categoriesRes.data.data || categoriesRes.data) as Category[];
        const teams = (teamsRes.data.data || teamsRes.data) as Team[];

        // Create lookup maps for faster access
        const categoryMap = new Map(
          categories.map((cat: Category) => [cat.id, cat.name_category])
        );
        const teamMap = new Map(
          teams.map((team: Team) => [team.id, team.name_team])
        );

        // Transform products to solutions format (only active products)
        const transformedSolutions = products
          .filter((product: Product) => product.is_active === true)
          .sort((a: Product, b: Product) => (a.display_order || 0) - (b.display_order || 0))
          .map((product: Product) => ({
            id: product.id,
            category: categoryMap.get(product.id_category) || t.product,
            title: product.name,
            teamName: teamMap.get(product.id_team) || "Team",
            description: product.description || "",
            buttonText: `${t.visit} ${product.name}`,
            features: product.key_features ? product.key_features.split(",").map((f: string) => f.trim()) : [],
            image: product.logo || "/solutions/default.svg",
            imageAlt: `${product.name} Solution`,
            isPlaceholder: false,
            link: product.slug,
          }));

        setSolutions(transformedSolutions);
      } catch (err) {
        console.log("Error fetching solutions:", err);
        setError("Failed to load solutions");
        setSolutions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSolutions();
  }, [language]);

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        {/* Navigation Bar */}
        <Header activePage="solutions" />

        {/* Hero Section */}
        <HeroSection
          title={heroContent?.content_title || "Our Solutions"}
          subtitle={heroContent?.content_description || "Comprehensive security and IT solutions tailored to protect your business"}
        />

        {/* Solutions Content Section */}
        <section className="py-20 px-8 lg:px-16 bg-white">
          <div ref={contentRef} className={`max-w-7xl mx-auto reveal ${contentInView ? 'is-visible' : ''}`}>
            {loading && (
              <div className="flex justify-center items-center py-20">
                <p className="text-lg text-gray-600">{t.loading}</p>
              </div>
            )}

            {error && (
              <div className="flex justify-center items-center py-20">
                <p className="text-lg text-red-600">{t.error}</p>
              </div>
            )}

            {!loading && !error && solutions.length === 0 && (
              <div className="flex justify-center items-center py-20">
                <p className="text-lg text-gray-600">{t.noSolutions}</p>
              </div>
            )}

            {!loading && !error && solutions.map((solution, index) => (
              <div key={`${solution.id}-${index}`}>
                {index > 0 && <div className="border-t border-black my-20"></div>}

                <div
                  className={`flex flex-col ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                    } items-center gap-12 reveal ${contentInView ? 'is-visible reveal-delay-100' : ''}`}
                >
                  {/* Text Content */}
                  <div
                    className={`flex-1 ${index % 2 === 0 ? "lg:text-left" : "lg:text-right"
                      } text-center`}
                  >
                    <span className="block text-sm font-semibold lg:font-normal text-black tracking-wide mb-1">
                      {solution.category}
                    </span>

                    {/* Logo/Icon */}
                    {/* <div className={`flex items-center gap-3 justify-center ${index % 2 === 0 ? "lg:justify-start" : "lg:justify-end"}`}>
                    <div className="w-25 h-25 flex items-center justify-center">
                      <Image src={solution.image}
                        alt={solution.imageAlt}
                        width={96}
                        height={96}
                        className="w-full h-full object-contain"
                       loading="lazy" />
                    </div>
                  </div> */}

                    <p className="text-black mb-6 font-semibold">
                      {/* <span className="font-semibold">Team Member: </span> */}
                      {solution.title}
                    </p>

                    {/* <p className="text-black mb-6">
                    <span className="font-semibold">Team Member: </span>
                    {solution.teamMember.join(", ")}
                  </p> */}

                    <p className="text-black mb-6 leading-relaxed">
                      {solution.description.split(" ").map((word, i) => (
                        <span key={`${solution.id}-word-${i}`}>{word} </span>
                      ))}
                    </p>
                    <div className={`flex justify-center ${index % 2 === 0 ? "lg:justify-start" : "lg:justify-end"}`}>
                      <a
                        href={solution.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          className="mb-8 rounded-[10px] px-6 py-6 text-white font-semibold text-lg transition-opacity hover:opacity-90 flex items-center gap-2"
                          style={{
                            background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                          }}
                        >
                          <span>{solution.buttonText}</span>
                          {/* <Image src="/homepage/homepage-picture/arrow-right.svg"
                            alt="Arrow"
                            width={24}
                            height={24}
                            className="w-6 h-6"
                            loading="lazy" /> */}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </Button>
                      </a>
                    </div>

                    <div>
                      <p className="text-black mb-4">{t.keyFeatures}</p>
                      <div className={`flex flex-wrap gap-2 justify-center ${index % 2 === 0 ? "lg:justify-start" : "lg:justify-end"}`}>
                        {solution.features.map((feature, idx) => (
                          <span
                            key={`${solution.id}-feature-${idx}`}
                            className="px-4 py-1 bg-white border border-black rounded-full text-sm text-black"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Image/Placeholder */}
                  <div className="flex-1 w-full">
                    <div className="relative w-full lg:aspect-video overflow-visible h-0 lg:h-auto">
                      {/* Back layer (blue bar) */}
                      <div className="hidden lg:block absolute bottom-20 -top-10 inset-y-6 left-0 right-0 rounded-[24px] bg-linear-to-b from-[#0C2E6C] via-[#285494] to-transparent" />

                      {/* Front layer (gray card overlapping) */}
                      <div className="hidden lg:block absolute -bottom-10 left-24 w-[72%] h-[70%] rounded-[28px] overflow-hidden shadow-lg">
                        {/* <div className="absolute inset-0 bg-transparent" />
                      <div className="absolute inset-0 bg-linear-to-r from-transparent via-transparent to-transparent" /> */}

                        <div className="absolute inset-0 bg-gray-300/60" />
                        <div className="absolute inset-0 bg-linear-to-r from-gray-300/80 via-gray-200/10 to-transparent" />
                      </div>

                      {/* Solution Image on top */}
                      <div className="hidden lg:block absolute -bottom-10 left-24 w-[72%] h-[70%] z-10">
                        <Image src={solution.image}
                          alt={solution.imageAlt}
                          fill
                          className="object-contain p-8"
                          unoptimized
                          loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}
